import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageHeader } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../overmind';
import { RoleAverageWidget } from '../../../components/general/average/role-average/role-average.widget';
import { RoleProficiencyRadarWidget } from './role-proficiency-radar.widget';
import { SkillScoreExpectedProficiencyWidget } from './skill-score-expected-proficiency.widget';
import { ScoresSearchParams } from '../../../models/overmind/search-params';
import { OrganizationLevelType, ScoreType } from '../../../enums';
import { ScoreComparison } from '../../../enums/score-comparison';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { OrganizationLevel } from '../../../models/overmind/permissions';

export function RolePersonPage(): React.ReactElement {
  const { roleSlug, personSlug } = useParams();

  const actions = useAppActions();
  const { companyVariables, user, permissions } = useAppState();
  const organizationLevelType: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );
  const organizationLevel: OrganizationLevel | undefined = React.useMemo(() => permissions?.analytics.organizationLevels.find(organizationLevel => organizationLevel.organizationLevel === organizationLevelType), [permissions?.analytics.organizationLevels, organizationLevelType]);

  const [roleName, setRoleName] = React.useState<string>('Role');
  const [personName, setPersonName] = React.useState<string>('Person');

  const baseSearchParams: ScoresSearchParams = React.useMemo(() => {
    const searchParams: ScoresSearchParams = {
      search: undefined,
      sortAscending: true,
      page: 1,
      pageSize: 10,
      startDate: undefined,
      endDate: undefined,
      organizationLevel: OrganizationLevelType.Learner,
      companySlug: companyVariables.slug,
      departmentSlugs: undefined,
      teamSlugs: undefined,
      learnerSlug: personSlug,
      scoreType: ScoreType.RoleSkill,
      scoreTypeSlugs: undefined,
      scoreComparison: ScoreComparison.RoleExpectedProficiency,
      roleSlug: roleSlug,
    };
    if (!!organizationLevel) {
      if (organizationLevelType === OrganizationLevelType.Department) searchParams.departmentSlugs = organizationLevel.entities.map(entity => entity.slug!);
      if (organizationLevelType === OrganizationLevelType.Team) searchParams.teamSlugs = organizationLevel.entities.map(entity => entity.slug!);
      if (organizationLevelType === OrganizationLevelType.Learner) searchParams.learnerSlug = user?.learnerSlug;
    }
    return searchParams;
  }, [companyVariables.slug, personSlug, roleSlug, organizationLevelType, organizationLevel]);

  useEffect(() => {
    async function getData() {
      const roleData = await actions.getCompanyRole({ companySlug: companyVariables.slug!, roleSlug: roleSlug! });
      setRoleName(roleData?.name ?? 'Role');

      const personData = await actions.getLearnerBySlug(personSlug!);
      setPersonName(personData?.fullName ?? 'Person');
    }

    getData();
  }, [roleSlug, personSlug]);

  return (
    <div className="wrapper stack" style={{ gap: 24 }}>
      <PageHeader
        breadcrumbs={[
          { name: `Analytics`, url: PagePath.analyticsBase },
          { name: `Roles`, url: `${PagePath.analyticsBase}${PagePath.analyticsRoles.replace(':companySlug', companyVariables.slug!)}` },
          { name: roleName, url: `${PagePath.analyticsBase}${PagePath.analyticsRole.replace(':companySlug', companyVariables.slug!).replace(':roleSlug', roleSlug!)}` },
          { name: personName, url: location.pathname },
        ]}
        title={personName}
        divider
      />

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 30 }}>
        <div style={{ flex: '1 1 25%' }}>
          <RoleAverageWidget baseSearchParams={baseSearchParams} personName={personName} />
        </div>

        <div style={{ flex: '2 1 630px' }}>
          <RoleProficiencyRadarWidget baseSearchParams={baseSearchParams} />
        </div>
      </div>

      <SkillScoreExpectedProficiencyWidget baseSearchParams={baseSearchParams} personName={personName} />
    </div>
  );
}
